import { Fragment, useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  supabase,
  TIER_LABEL,
  TOOLKIT_LABEL,
  type PartnerTier,
  type Resource,
  type ResourceKind,
  type ResourceSection,
  type Toolkit,
} from '@/lib/supabase'
import { Badge, Button, Card, ErrorNote, PageHeader } from '@/components/ui'
import { ResourceFileSection } from './ResourceFileSection'

const TOOLKITS: Toolkit[] = ['programme', 'training', 'promotion', 'impact']
const TIERS: PartnerTier[] = ['core', 'supported', 'partner_plus']
const KINDS: ResourceKind[] = ['document', 'video', 'template']

// Sections are stored (resource_sections), created and ordered by Ki Tua o
// Matariki, and shown in the same order in the partner library. A resource
// whose section was deleted lands in "Not in a section" at the bottom.
const UNSECTIONED = 'Not in a section'

interface ResourceGroup {
  section: ResourceSection | null
  items: Resource[]
}

function groupResources(sections: ResourceSection[], rows: Resource[]): ResourceGroup[] {
  const bySection = new Map<string | null, Resource[]>()
  for (const row of rows) {
    const key = row.section_id && sections.some((sec) => sec.id === row.section_id) ? row.section_id : null
    bySection.set(key, [...(bySection.get(key) ?? []), row])
  }
  const byOrder = (a: Resource, b: Resource) => a.sort_order - b.sort_order || a.title.localeCompare(b.title)
  // Every section is listed, even an empty one, so it can be filled or deleted.
  const groups: ResourceGroup[] = sections.map((section) => ({
    section,
    items: (bySection.get(section.id) ?? []).sort(byOrder),
  }))
  const loose = bySection.get(null)
  if (loose?.length) groups.push({ section: null, items: loose.sort(byOrder) })
  return groups
}

/** Swap an item with its neighbour; returns the new id order, or null at an edge. */
function moved<T extends { id: string }>(items: T[], index: number, step: -1 | 1): string[] | null {
  const target = index + step
  if (target < 0 || target >= items.length) return null
  const ids = items.map((i) => i.id)
  ;[ids[index], ids[target]] = [ids[target], ids[index]]
  return ids
}

/** Position for a resource added to the end of a section. */
function nextSortOrder(rows: Resource[], sectionId: string | null): number {
  const inSection = rows.filter((r) => r.section_id === sectionId)
  return inSection.reduce((max, r) => Math.max(max, r.sort_order), 0) + 1
}

// A resource with neither storage_path nor video_url is "metadata only" - a
// draft record that cannot be published until a file is added. The badge
// makes the state visible so nobody wonders why the publish toggle is stuck.
function isMetadataOnly(row: Pick<Resource, 'storage_path' | 'video_url'>): boolean {
  return !row.storage_path && !row.video_url
}

interface ResourceFormValues {
  title: string
  description: string
  toolkit: Toolkit
  min_tier: PartnerTier
  kind: ResourceKind
  version: string
  version_notes: string
  is_published: boolean
  section_id: string
}

function emptyForm(): ResourceFormValues {
  return {
    title: '',
    description: '',
    toolkit: 'programme',
    min_tier: 'core',
    kind: 'document',
    version: '1.0',
    version_notes: '',
    is_published: false,
    section_id: '',
  }
}

function toForm(row: Resource): ResourceFormValues {
  return {
    title: row.title,
    description: row.description ?? '',
    toolkit: row.toolkit,
    min_tier: row.min_tier,
    kind: row.kind,
    version: row.version,
    version_notes: row.version_notes ?? '',
    is_published: row.is_published,
    section_id: row.section_id ?? '',
  }
}

/**
 * Work plan 3.3, 3.4, R13, R14, R19. Metadata plus the attached file. The
 * publish toggle is held off until a body exists (constraint
 * resources_published_needs_body).
 */
export function ResourcesPage() {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<
    { kind: 'list' } | { kind: 'new' } | { kind: 'edit'; row: Resource }
  >({ kind: 'list' })

  const { data: resources, isLoading } = useQuery({
    queryKey: ['admin', 'resources'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('resources')
        .select(
          'id, title, description, toolkit, min_tier, kind, version, version_notes, published_at, is_published, storage_path, file_size_bytes, video_url, section_id, sort_order',
        )
        .order('sort_order')
        .order('title')
      if (error) throw error
      return data as Resource[]
    },
  })

  const { data: sections } = useQuery({
    queryKey: ['admin', 'resource-sections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('resource_sections')
        .select('id, title, description, sort_order')
        .order('sort_order')
        .order('title')
      if (error) throw error
      return data as ResourceSection[]
    },
  })

  const backToList = () => setMode({ kind: 'list' })
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'resources'] })
  const refreshSections = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'resource-sections'] })

  if (mode.kind === 'new') {
    return (
      <ResourceEditor
        title="New resource"
        sections={sections ?? []}
        initial={emptyForm()}
        submitLabel="Create"
        onCancel={backToList}
        onSubmit={async (values) => {
          const { data, error } = await supabase.from('resources').insert({
            title: values.title,
            description: values.description || null,
            toolkit: values.toolkit,
            min_tier: values.min_tier,
            kind: values.kind,
            version: values.version,
            version_notes: values.version_notes || null,
            is_published: false, // A new resource has no body yet.
            section_id: values.section_id || null,
            sort_order: nextSortOrder(resources ?? [], values.section_id || null),
          }).select().single()
          if (error) throw error
          await refresh()
          // Straight to the edit screen, where the file is attached.
          setMode({ kind: 'edit', row: data as Resource })
        }}
        // A new row has neither body nor id: publish is off the table for now.
        publishBlockedReason="Create the resource, then attach its file on the next screen before publishing."
      />
    )
  }

  if (mode.kind === 'edit') {
    const row = mode.row
    const metadataOnly = isMetadataOnly(row)
    return (
      <ResourceEditor
        key={row.id}
        title={row.title}
        sections={sections ?? []}
        fileSection={
          <ResourceFileSection
            row={row}
            onChange={(updated) => {
              setMode({ kind: 'edit', row: updated })
              void refresh()
            }}
          />
        }
        initial={toForm(row)}
        submitLabel="Save"
        onCancel={backToList}
        onSubmit={async (values) => {
          const patch: Partial<Resource> = {
            title: values.title,
            description: values.description || null,
            toolkit: values.toolkit,
            min_tier: values.min_tier,
            kind: values.kind,
            version: values.version,
            version_notes: values.version_notes || null,
            is_published: values.is_published,
            section_id: values.section_id || null,
          }
          if ((values.section_id || null) !== row.section_id) {
            patch.sort_order = nextSortOrder(resources ?? [], values.section_id || null)
          }
          // published_at stamped on the first publish, cleared on unpublish.
          if (values.is_published && !row.is_published) {
            patch.published_at = new Date().toISOString()
          }
          if (!values.is_published && row.is_published) {
            patch.published_at = null
          }
          const { error } = await supabase.from('resources').update(patch).eq('id', row.id)
          if (error) throw error
          await refresh()
          backToList()
        }}
        onDelete={async () => {
          const { error } = await supabase.from('resources').delete().eq('id', row.id)
          if (error) throw error
          await refresh()
          backToList()
        }}
        deleteConfirm={`Delete "${row.title}"? This removes the record for every partner. The audit log keeps the history.`}
        publishBlockedReason={
          metadataOnly
            ? 'Attach a file above before publishing.'
            : undefined
        }
      />
    )
  }

  return (
    <>
      <PageHeader title="Resource administration" >
        Create and edit every resource in the library and attach its file. Records without a
        file stay as drafts until one is uploaded.
      </PageHeader>

      <div className="mb-4 flex flex-wrap items-start gap-3">
        <Button onClick={() => setMode({ kind: 'new' })}>New resource</Button>
        <NewSectionForm
          nextOrder={(sections ?? []).reduce((m, sec) => Math.max(m, sec.sort_order), 0) + 1}
          onAdded={refreshSections}
        />
      </div>

      {isLoading && <p className="text-sm text-inksoft">Loading…</p>}

      <div className="overflow-x-auto rounded-md border border-line bg-surface">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-inksoft">
            <tr>
              <th className="px-4 py-2 font-medium">Title</th>
              <th className="px-4 py-2 font-medium">Toolkit</th>
              <th className="px-4 py-2 font-medium">Tier</th>
              <th className="px-4 py-2 font-medium">Kind</th>
              <th className="px-4 py-2 font-medium">Version</th>
              <th className="px-4 py-2 font-medium">State</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupResources(sections ?? [], resources ?? []).map((group, groupIndex, groups) => (
              <Fragment key={group.section?.id ?? 'unsectioned'}>
                <SectionHeaderRow
                  section={group.section}
                  count={group.items.length}
                  onMove={
                    group.section
                      ? (step) => {
                          const ordered = groups.flatMap((g) => (g.section ? [g.section] : []))
                          return moved(ordered, groupIndex, step)
                        }
                      : undefined
                  }
                  onChanged={() => {
                    void refreshSections()
                    void refresh()
                  }}
                />
                {group.items.map((row, rowIndex) => {
                  const metadataOnly = isMetadataOnly(row)
                  return (
                    <tr key={row.id} className="border-b border-line last:border-0">
                      <td className="px-4 py-2">
                        <div>{row.title}</div>
                        {row.description && (
                          <div className="line-clamp-1 text-xs text-inksoft">{row.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-inksoft">{TOOLKIT_LABEL[row.toolkit]}</td>
                      <td className="px-4 py-2 text-inksoft">{TIER_LABEL[row.min_tier]}</td>
                      <td className="px-4 py-2 text-inksoft">{row.kind}</td>
                      <td className="px-4 py-2 text-inksoft">{row.version}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {row.is_published ? (
                          <Badge tone="good">Published</Badge>
                        ) : metadataOnly ? (
                          <Badge tone="locked">Metadata only</Badge>
                        ) : (
                          <Badge>Draft</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <MoveButtons
                          label={row.title}
                          canUp={rowIndex > 0}
                          canDown={rowIndex < group.items.length - 1}
                          onMove={async (step) => {
                            const ids = moved(group.items, rowIndex, step)
                            if (!ids) return
                            const { error } = await supabase.rpc('reorder_resources', { p_ids: ids })
                            if (error) throw error
                            await refresh()
                          }}
                        />
                        <Button variant="quiet" onClick={() => setMode({ kind: 'edit', row })}>
                          Edit
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </Fragment>
            ))}
            {(resources ?? []).length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-inksoft">
                  No resources yet. Create one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Editor form, reused for create and edit.
// ---------------------------------------------------------------------------
function ResourceEditor({
  title,
  sections,
  initial,
  submitLabel,
  onCancel,
  onSubmit,
  onDelete,
  deleteConfirm,
  publishBlockedReason,
  fileSection,
}: {
  title: string
  sections: ResourceSection[]
  initial: ResourceFormValues
  submitLabel: string
  onCancel: () => void
  onSubmit: (values: ResourceFormValues) => Promise<void>
  onDelete?: () => Promise<void>
  deleteConfirm?: string
  publishBlockedReason?: string
  fileSection?: ReactNode
}) {
  const [values, setValues] = useState<ResourceFormValues>(initial)
  const [error, setError] = useState<string | null>(null)
  const publishBlocked = Boolean(publishBlockedReason)

  const save = useMutation({
    mutationFn: async () => {
      if (!values.title.trim()) throw new Error('Give the resource a title.')
      // Belt and braces: the DB constraint would block this too, but the UI
      // should not let you submit a state we know will bounce.
      if (values.is_published && publishBlocked) {
        throw new Error(publishBlockedReason ?? 'This resource is not ready to publish.')
      }
      await onSubmit(values)
    },
    onError: (e: Error) => setError(e.message),
  })

  const remove = useMutation({
    mutationFn: async () => {
      if (!onDelete) return
      if (!window.confirm(deleteConfirm ?? 'Delete this resource?')) return
      await onDelete()
    },
    onError: (e: Error) => setError(e.message),
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    save.mutate()
  }

  function update<K extends keyof ResourceFormValues>(key: K, value: ResourceFormValues[K]) {
    setValues((v) => ({ ...v, [key]: value }))
  }

  return (
    <>
      <PageHeader title={title} >
        {fileSection
          ? 'The file partners download, then the details they see in the library.'
          : 'Details first. The file is attached on the next screen.'}
      </PageHeader>

      {fileSection}

      <Card className="max-w-3xl">
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm sm:col-span-2">
            <span className="font-medium text-ink">Title</span>
            <input
              value={values.title}
              onChange={(e) => update('title', e.target.value)}
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
              required
            />
          </label>

          <label className="block text-sm sm:col-span-2">
            <span className="font-medium text-ink">Section</span>
            <select
              value={values.section_id}
              onChange={(e) => update('section_id', e.target.value)}
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
            >
              <option value="">{UNSECTIONED}</option>
              {sections.map((sec) => (
                <option key={sec.id} value={sec.id}>{sec.title}</option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-inksoft">
              Where it is listed in the library. Moving it to another section puts it at the end
              of that section.
            </span>
          </label>

          <label className="block text-sm sm:col-span-2">
            <span className="font-medium text-ink">Description</span>
            <textarea
              value={values.description}
              onChange={(e) => update('description', e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
              placeholder="One or two sentences a partner will see in the library."
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-ink">Toolkit</span>
            <select
              value={values.toolkit}
              onChange={(e) => update('toolkit', e.target.value as Toolkit)}
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
            >
              {TOOLKITS.map((t) => (
                <option key={t} value={t}>{TOOLKIT_LABEL[t]}</option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-ink">Minimum tier</span>
            <select
              value={values.min_tier}
              onChange={(e) => update('min_tier', e.target.value as PartnerTier)}
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
            >
              {TIERS.map((t) => (
                <option key={t} value={t}>{TIER_LABEL[t]}</option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-ink">Kind</span>
            <select
              value={values.kind}
              onChange={(e) => update('kind', e.target.value as ResourceKind)}
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-ink">Version</span>
            <input
              value={values.version}
              onChange={(e) => update('version', e.target.value)}
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
              required
            />
          </label>

          <label className="block text-sm sm:col-span-2">
            <span className="font-medium text-ink">Version notes</span>
            <textarea
              value={values.version_notes}
              onChange={(e) => update('version_notes', e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
              placeholder="What changed in this version. R19: partners are notified when this updates."
            />
          </label>

          <div className="sm:col-span-2">
            <div className="rounded-sm border border-line bg-paper px-4 py-3">
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={values.is_published}
                  onChange={(e) => update('is_published', e.target.checked)}
                  disabled={publishBlocked}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium text-ink">Published</span>
                  <span className="block text-xs text-inksoft">
                    A published resource appears in the library for every partner whose tier and
                    subscription allow it.
                  </span>
                  {publishBlocked && (
                    <span className="mt-2 block text-xs text-gold">{publishBlockedReason}</span>
                  )}
                </span>
              </label>
            </div>
          </div>

          {error && (
            <div className="sm:col-span-2">
              <ErrorNote>{error}</ErrorNote>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : submitLabel}
            </Button>
            <Button type="button" variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
            {onDelete && (
              <button
                type="button"
                onClick={() => {
                  setError(null)
                  remove.mutate()
                }}
                disabled={remove.isPending}
                className="ml-auto text-sm text-alert underline underline-offset-4 hover:no-underline disabled:opacity-60"
              >
                {remove.isPending ? 'Deleting…' : 'Delete resource'}
              </button>
            )}
          </div>
        </form>
      </Card>
    </>
  )
}

// ---------------------------------------------------------------------------
// Section management
// ---------------------------------------------------------------------------
function NewSectionForm({ nextOrder, onAdded }: { nextOrder: number; onAdded: () => void }) {
  const [title, setTitle] = useState('')
  const [error, setError] = useState<string | null>(null)

  const add = useMutation({
    mutationFn: async () => {
      const trimmed = title.trim()
      if (!trimmed) throw new Error('Give the section a name.')
      const { error: insertError } = await supabase
        .from('resource_sections')
        .insert({ title: trimmed, sort_order: nextOrder })
      if (insertError) {
        throw new Error(
          insertError.code === '23505' ? 'There is already a section with that name.' : insertError.message,
        )
      }
    },
    onSuccess: () => {
      setTitle('')
      setError(null)
      onAdded()
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        add.mutate()
      }}
      className="flex flex-wrap items-center gap-2"
    >
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="New section name"
        aria-label="New section name"
        className="w-56 rounded-sm border border-line px-3 py-2 text-sm"
      />
      <Button type="submit" variant="secondary" disabled={add.isPending}>
        {add.isPending ? 'Adding…' : 'Add section'}
      </Button>
      {error && <span className="w-full"><ErrorNote>{error}</ErrorNote></span>}
    </form>
  )
}

function SectionHeaderRow({
  section,
  count,
  onMove,
  onChanged,
}: {
  section: ResourceSection | null
  count: number
  /** Returns the new section id order for a step, or null at an edge. */
  onMove?: (step: -1 | 1) => string[] | null
  onChanged: () => void
}) {
  const [renaming, setRenaming] = useState(false)
  const [title, setTitle] = useState(section?.title ?? '')
  const [error, setError] = useState<string | null>(null)

  const rename = useMutation({
    mutationFn: async () => {
      const trimmed = title.trim()
      if (!section || !trimmed) throw new Error('Give the section a name.')
      const { error: updateError } = await supabase
        .from('resource_sections')
        .update({ title: trimmed })
        .eq('id', section.id)
      if (updateError) {
        throw new Error(
          updateError.code === '23505' ? 'There is already a section with that name.' : updateError.message,
        )
      }
    },
    onSuccess: () => {
      setRenaming(false)
      setError(null)
      onChanged()
    },
    onError: (e: Error) => setError(e.message),
  })

  const remove = useMutation({
    mutationFn: async () => {
      if (!section) return
      const note = count > 0 ? ` Its ${count} resource${count === 1 ? '' : 's'} will move to "${UNSECTIONED}".` : ''
      if (!window.confirm(`Delete the section "${section.title}"?${note}`)) return
      const { error: deleteError } = await supabase.from('resource_sections').delete().eq('id', section.id)
      if (deleteError) throw deleteError
      onChanged()
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <tr className="bg-pounamuwash">
      <th colSpan={7} scope="colgroup" className="px-4 py-2 text-left text-sm font-medium text-pounamu">
        <div className="flex flex-wrap items-center gap-3">
          {renaming ? (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                rename.mutate()
              }}
              className="flex items-center gap-2"
            >
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                aria-label="Section name"
                autoFocus
                className="rounded-sm border border-line bg-surface px-2 py-1 font-normal text-ink"
              />
              <Button type="submit" variant="quiet" className="px-0" disabled={rename.isPending}>Save</Button>
              <Button
                type="button"
                variant="quiet"
                className="px-0"
                onClick={() => {
                  setRenaming(false)
                  setTitle(section?.title ?? '')
                  setError(null)
                }}
              >
                Cancel
              </Button>
            </form>
          ) : (
            <span>
              {section?.title ?? UNSECTIONED}{' '}
              <span className="font-normal text-inksoft">({count})</span>
            </span>
          )}

          {section && !renaming && (
            <span className="ml-auto flex items-center gap-3 font-normal">
              {onMove && (
                <MoveButtons
                  label={section.title}
                  canUp={onMove(-1) !== null}
                  canDown={onMove(1) !== null}
                  onMove={async (step) => {
                    const ids = onMove(step)
                    if (!ids) return
                    const { error: rpcError } = await supabase.rpc('reorder_resource_sections', { p_ids: ids })
                    if (rpcError) throw rpcError
                    onChanged()
                  }}
                />
              )}
              <Button variant="quiet" className="px-0" onClick={() => setRenaming(true)}>Rename</Button>
              <Button variant="quiet" className="px-0" disabled={remove.isPending} onClick={() => remove.mutate()}>
                Delete
              </Button>
            </span>
          )}
        </div>
        {error && <div className="mt-2 font-normal"><ErrorNote>{error}</ErrorNote></div>}
      </th>
    </tr>
  )
}

function MoveButtons({
  label,
  canUp,
  canDown,
  onMove,
}: {
  label: string
  canUp: boolean
  canDown: boolean
  onMove: (step: -1 | 1) => Promise<void>
}) {
  const move = useMutation({
    mutationFn: onMove,
    onError: (e: Error) => window.alert(`Could not move "${label}": ${e.message}`),
  })
  const style =
    'rounded-sm px-1.5 py-0.5 text-inksoft hover:bg-surface hover:text-ink disabled:cursor-not-allowed disabled:opacity-30'
  return (
    <span className="mr-3 inline-flex gap-0.5 align-middle">
      <button
        type="button"
        className={style}
        disabled={!canUp || move.isPending}
        onClick={() => move.mutate(-1)}
        aria-label={`Move "${label}" up`}
        title="Move up"
      >
        ↑
      </button>
      <button
        type="button"
        className={style}
        disabled={!canDown || move.isPending}
        onClick={() => move.mutate(1)}
        aria-label={`Move "${label}" down`}
        title="Move down"
      >
        ↓
      </button>
    </span>
  )
}
